import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { Loader2, Filter, X, ArrowRight, ArrowUpRight, Compass, ChevronDown, ChevronUp } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import KnowledgeHeroAnimation from "@/components/KnowledgeHeroAnimation";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import ResourceCard from "@/components/resources/ResourceCard";
import BlogFilterSidebar, { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";
import BlogSearch from "@/components/blog/BlogSearch";
import BlogCardSkeleton from "@/components/blog/BlogCardSkeleton";
import FeaturedPost from "@/components/blog/FeaturedPost";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getContentTypeMeta, EXCLUDED_TAGS, type ContentTypeKey, type UnifiedResource } from "@/lib/resourceTypes";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeContentRow } from "@/lib/localizeContent";
import { localizeHref } from "@/lib/localizeHref";
import { getKCT } from "@/i18n/knowledgeCenter";
import { uiT } from "@/i18n/ui";
import { expandFilterAliases } from "@/lib/filterAliases";
import { getOnlineTools, getRecentOnlineTools, onlineToolsCopy, toolVisual, RECENT_TOOL_COUNT } from "@/data/onlineTools";

const PAGE_SIZE = 13;

interface ContentListingProps {
  contentType: ContentTypeKey;
  pageTitle: string;
  pageDescription: string;
  singleSelectFilter?: boolean;
  showAssessments?: boolean;
}

const ContentListing = ({ contentType, pageTitle, pageDescription, singleSelectFilter = false, showAssessments = false }: ContentListingProps) => {
  const meta = getContentTypeMeta(contentType);
  const lang = useLanguage();
  const kc = getKCT("contentListing", lang);
  const t = uiT(lang);
  const toolsCopy = onlineToolsCopy[lang] ?? onlineToolsCopy.en;
  const hasEsCols = contentType !== "blog_posts";
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter");
  const [selectedFilters, setSelectedFilters] = useState<string[]>(initialFilter ? [initialFilter] : []);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showAllTools, setShowAllTools] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const allTools = getOnlineTools(lang);
  const visibleTools = showAllTools ? allTools : getRecentOnlineTools(lang);
  const isRegulatoryUpdates = contentType === "regulatory_updates";
  const useSingleSelect = singleSelectFilter || isRegulatoryUpdates;

  // Reset filters when content type changes
  useEffect(() => {
    setSelectedFilters(initialFilter ? [initialFilter] : []);
    setSearchQuery("");
  }, [contentType]);

  // These tables support a Chinese variant via *_zh columns; only show ones with a Chinese title.
  const hasZhCols = contentType === "white_papers" || contentType === "checklists" || contentType === "case_studies";

  // Fetch featured post
  const { data: featuredPost } = useQuery({
    queryKey: [`${contentType}-featured`, lang],
    queryFn: async () => {
      const cols = "id, slug, title, summary, featured_image, category, published_at, author, content, tags"
        + (hasEsCols ? ", title_es, summary_es, content_es, slug_es" : "")
        + (hasZhCols ? ", title_zh, summary_zh, content_zh, slug_zh" : "");
      let featQuery: any = supabase
        .from(contentType as any)
        .select(cols)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(1);
      if (contentType === "blog_posts") {
        featQuery = featQuery.eq("lang", lang === "es" ? "es" : lang === "zh-cn" ? "zh-cn" : "en");
      } else if (lang === "zh-cn") {
        if (hasZhCols) {
          // Only show items that have a Chinese title
          featQuery = featQuery.not("title_zh", "is", null);
        } else {
          // No ZH translations exist yet for this content type — return nothing
          return null;
        }
      }
      const { data, error } = await featQuery.maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const featuredTags = (data as any).tags ?? [];
      const featuredCategory = (data as any).category ?? "";
      const featuredHidden = EXCLUDED_TAGS.some(
        (ex) => featuredCategory.toLowerCase() === ex.toLowerCase() || featuredTags.some((tag: string) => tag.toLowerCase() === ex.toLowerCase())
      );
      if (featuredHidden) return null;
      const localized = localizeContentRow(data as Record<string, any>, lang);
      return { ...localized, contentType } as UnifiedResource;
    },
  });

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [`${contentType}-listing`, selectedFilters, searchQuery, lang],
    queryFn: async ({ pageParam = 0 }) => {
      const cols = "id, slug, title, summary, featured_image, category, industry_category, service_category, published_at, author, tags, content"
        + (hasEsCols ? ", title_es, summary_es, content_es, slug_es" : "")
        + (hasZhCols ? ", title_zh, summary_zh, content_zh, slug_zh" : "");
      let query: any = supabase
        .from(contentType as any)
        .select(cols)
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);

      // blog_posts uses a lang column; other tables use _es columns
      if (contentType === "blog_posts") {
        query = query.eq("lang", lang === "es" ? "es" : lang === "zh-cn" ? "zh-cn" : "en");
      } else if (lang === "zh-cn") {
        if (hasZhCols) {
          // Only show items that have a Chinese title
          query = query.not("title_zh", "is", null);
        } else {
          // No ZH translations exist yet for these content types — return empty
          return { posts: [], nextOffset: undefined };
        }
      }

      if (selectedFilters.length > 0) {
        // AND logic: each selected filter must match independently
        for (const filter of selectedFilters) {
          const expanded = expandFilterAliases([filter]);
          const industryClauses = isRegulatoryUpdates
            ? expanded.map((f) => `industry_category.ilike.%${f}%`)
            : expanded.map((f) => `industry_category.eq.${f}`);
          const tagClauses = expanded.map((f) => `tags.cs.{"${f}"}`);
          const orClauses = [...industryClauses, ...tagClauses, ...(!isRegulatoryUpdates ? expanded.map((f) => `service_category.eq.${f}`) : [])].join(",");
          query = query.or(orClauses);
        }
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,summary.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      const posts = (data ?? [])
        .filter((row: any) => {
          const tags = row.tags ?? [];
          const category = row.category ?? "";
          return !EXCLUDED_TAGS.some(
            (ex) => category.toLowerCase() === ex.toLowerCase() || tags.some((tag: string) => tag.toLowerCase() === ex.toLowerCase())
          );
        })
        .map((row: any) => ({ ...localizeContentRow(row, lang), contentType } as UnifiedResource));
      return {
        posts,
        nextOffset: data && data.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    initialPageParam: 0,
  });

  const { data: allFilters } = useQuery({
    queryKey: [`${contentType}-filters`, lang],
    queryFn: async () => {
      let filterQuery = supabase
        .from(contentType as any)
        .select("industry_category, service_category, tags");
      if (contentType === "blog_posts") {
        filterQuery = filterQuery.eq("lang", lang === "es" ? "es" : lang === "zh-cn" ? "zh-cn" : "en");
      } else if (lang === "zh-cn") {
        if (hasZhCols) {
          filterQuery = filterQuery.not("title_zh", "is", null);
        } else {
          return [];
        }
      }
      const { data, error } = await filterQuery;
      if (error) throw error;
      const excludedLower = EXCLUDED_TAGS.map((e) => e.toLowerCase());
      const cats = (data as any[]).flatMap((d) => [d.industry_category, d.service_category]).filter((c) => c && !excludedLower.includes(c.toLowerCase()));
      const tags = (data as any[]).flatMap((d) => d.tags || []).filter((t) => !excludedLower.includes(t.toLowerCase()));
      return [...new Set([...cats, ...tags])].sort();
    },
  });

  const allPosts = data?.pages.flatMap((page) => page.posts) ?? [];
  const showFeatured = selectedFilters.length === 0 && !searchQuery && featuredPost;
  const posts = showFeatured
    ? allPosts.filter((p) => p.id !== featuredPost?.id)
    : allPosts;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = useCallback((q: string) => setSearchQuery(q), []);

  const canonicalUrl = `https://qbdgroup.com${meta.route}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${pageTitle} | QbD Group`,
    description: pageDescription,
    url: canonicalUrl,
    publisher: {
      "@type": "Organization",
      name: "QbD Group",
      url: "https://qbdgroup.com",
    },
  };

  // Build language-aware link prefix for cards
  const localizedRoute = localizeHref(meta.route, lang);
  // For the featured post component we need to map to the blog-style link
  const featuredForComponent = showFeatured && featuredPost ? {
    ...featuredPost,
    _linkOverride: `${localizedRoute}/${featuredPost.slug}`,
  } : null;

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />
      <SEOHead
        title={`${pageTitle} | QbD Group`}
        description={pageDescription}
        canonical={canonicalUrl}
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </SEOHead>
      <BreadcrumbJsonLd items={[{ name: kc.breadcrumbKC, path: localizeHref("/en/knowledge-center", lang) }, { name: pageTitle }]} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 sm:pb-28 bg-primary overflow-hidden">
        <KnowledgeHeroAnimation />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent/5 -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <nav aria-label="Breadcrumb" className="mb-8 mt-4">
              <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
                <li>
                  <a href={localizeHref("/", lang)} className="hover:text-primary-foreground/70 transition-colors">{t("home")}</a>
                </li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li>
                  <a href={localizeHref("/en/knowledge-center", lang)} className="hover:text-primary-foreground/70 transition-colors">{kc.breadcrumbKC}</a>
                </li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li className="text-primary-foreground/60">{pageTitle}</li>
              </ol>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight leading-[1.1]">
              {pageTitle}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-primary-foreground/60 max-w-xl leading-relaxed">
              {pageDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-qbd-ice">
        <div className="container mx-auto px-6 lg:px-10">
          {/* Top bar */}
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden gap-2"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <Filter size={14} />
                {kc.filters}
              </Button>
              {selectedFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setSelectedFilters(selectedFilters.filter((x) => x !== f))}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-primary/8 text-primary rounded-full hover:bg-primary/15 transition-colors"
                >
                  {FILTER_DISPLAY_NAMES[f] ?? f}
                  <X size={12} />
                </button>
              ))}
              {selectedFilters.length > 1 && (
                <button
                  onClick={() => setSelectedFilters([])}
                  className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                   {kc.clearAll}
                </button>
              )}
            </div>
            <span className="text-sm text-muted-foreground shrink-0">
              {allPosts.length > 0 && `${allPosts.length}+ ${kc.articlesSuffix ?? "articles"}`}
            </span>
          </div>

          {/* Mobile filters */}
          {showMobileFilters && allFilters && (
            <div className="lg:hidden mb-6 bg-background border border-border rounded-xl p-4">
              <BlogFilterSidebar
                selectedFilters={selectedFilters}
                onFilterChange={(f) => {
                  setSelectedFilters(f);
                  setShowMobileFilters(false);
                }}
                availableFilters={allFilters}
                singleSelect={useSingleSelect}
                industriesOnly={isRegulatoryUpdates}
              />
            </div>
          )}

          <div className="flex gap-10">
            {/* Desktop sidebar */}
            <div className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-36 space-y-6">
                <BlogSearch onSearch={handleSearch} placeholder={kc.searchPlaceholder ?? "Search articles..."} />
                {allFilters && (
                  <BlogFilterSidebar
                    selectedFilters={selectedFilters}
                    onFilterChange={setSelectedFilters}
                    availableFilters={allFilters}
                    singleSelect={useSingleSelect}
                    industriesOnly={isRegulatoryUpdates}
                  />
                )}
              </div>
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {showAssessments && (
                <div className="mb-10 rounded-2xl border border-qbd-sky bg-qbd-ice/70 p-6 lg:p-8">
                  <div className="mb-5">
                    <h2 className="text-2xl font-bold text-foreground">{toolsCopy.heading}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{toolsCopy.intro}</p>
                  </div>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {visibleTools.map((a) => {
                      const Icon = (toolVisual[a.href] ?? { icon: Compass }).icon;
                      return (
                        <a
                          key={a.href}
                          href={a.href}
                          {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="group flex flex-col rounded-xl border border-border bg-background p-6 shadow-sm hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1 hover:border-primary/40 transition-all duration-300"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110">
                              <Icon size={20} strokeWidth={1.75} />
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/70">
                                {toolsCopy.badge}
                              </span>
                              {a.external && <ArrowUpRight size={14} className="text-muted-foreground" />}
                            </span>
                          </div>
                          <h3 className="text-base font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                            {a.title}
                          </h3>
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                            {a.desc}
                          </p>
                          <span className="mt-auto pt-5 self-start inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary transition-transform duration-300 group-hover:translate-x-1">
                            {toolsCopy.cta}
                            <ArrowRight size={14} />
                          </span>
                        </a>
                      );
                    })}
                  </div>
                  {allTools.length > RECENT_TOOL_COUNT && (
                    <div className="mt-6 flex justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setShowAllTools((v) => !v)}
                        className="gap-2"
                        aria-expanded={showAllTools}
                      >
                        {showAllTools ? toolsCopy.showLess : toolsCopy.seeAll}
                        {showAllTools ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {showAssessments && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-foreground">{kc.downloadsHeading}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{kc.downloadsIntro}</p>
                </div>
              )}

              {/* Featured post */}
              {featuredForComponent && (
                <FeaturedPost post={featuredForComponent} linkPrefix={localizedRoute} />
              )}

              {isLoading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg mb-2">
                    {searchQuery
                      ? `${kc.noResults} "${searchQuery}".`
                      : selectedFilters.length > 0
                      ? kc.noFilters
                      : kc.noPosts}
                  </p>
                  {(searchQuery || selectedFilters.length > 0) && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedFilters([]);
                      }}
                      className="text-sm text-qbd-blue font-semibold hover:underline"
                    >
                      {kc.clearAll}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {posts.map((post, i) => (
                      <ResourceCard key={post.id} resource={post} index={i} hideContentTypeLabel />
                    ))}
                  </div>
                  <div ref={loadMoreRef} className="flex justify-center py-10">
                    {isFetchingNextPage && (
                      <Loader2 className="animate-spin text-muted-foreground" size={24} />
                    )}
                    {!hasNextPage && posts.length > PAGE_SIZE && (
                      <p className="text-sm text-muted-foreground">{kc.reachedEnd}</p>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContentListing;
